import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Scroll from "react-scroll";
import { toast } from "react-toastify";
import {
  ProjectTreesContributionAggregate,
  User,
  UserType,
} from "@ecologi/shared";

type HookReturn = {
  isSuccess: boolean;
  resetProfileParamsState: () => void;
};

export const useProfileHooks = ({
  isCarbonOnly,
  isError,
  isSuccess,
  profile,
}: {
  isCarbonOnly: boolean;
  isError: boolean;
  isOwnProfile: boolean;
  isSuccess: boolean;
  profile: User;
}): HookReturn => {
  const router = useRouter();

  // Store the success in it's own state so we're not reliant on router state
  const [routerState, setRouterState] = useState<{
    isSuccess: boolean;
    isError: boolean;
    isCarbonOnly: boolean;
  } | null>(null);

  useEffect(() => {
    // Save the router state only if it's not set already. This acts
    // kind of like "onMount" and allows us to capture the URL state
    // without being bound to the actual router. We can them remove the params (below)
    if (!routerState && (isSuccess || isError)) {
      setRouterState({
        isSuccess,
        isError,
        isCarbonOnly,
      });
    }

    // Delete success/error query params to avoid duplicate toasting
    if (isSuccess || isError) {
      router.replace(window.location.pathname, undefined, { shallow: true });
    }
  }, [isSuccess, isError, isCarbonOnly, profile]);

  // Show a nice message and scroll the user to the forest
  // after they've purchased trees and/or carbon. Now using the
  // the stored state, rather than the direct route state
  useEffect(() => {
    if (!routerState) {
      return;
    }

    if (routerState.isSuccess) {
      toast.success("Purchase complete, thank you!", { delay: 2000 });

      setTimeout(() => {
        Scroll.scroller.scrollTo("your-tile", {
          duration: 500,
          offset: -200,
          smooth: true,
        });
      }, 3000);
    }
    if (routerState.isError) {
      toast.error(
        "Something went wrong! You shouldn’t have been charged, but please check your bank account and try again.",
        { delay: 2000 }
      );
    }
  }, [routerState]);

  return {
    isSuccess: routerState?.isSuccess || false,
    resetProfileParamsState: () => setRouterState(null),
  };
};

export type ImpactPropertiesReturn = {
  projectTreesContributionAggregate: ProjectTreesContributionAggregate[];
  totalCarbonTonnes: number;
  totalTrees: number;
  totalTreeTransactions: number;
};

export const useCombineLatestImpactWithProfileImpact = ({
  projectTreesContributionAggregate,
  totalTrees,
  totalTreeTransactions,
  totalCarbonTonnes,
  profile,
}: {
  profile: UserType;
  projectTreesContributionAggregate: string;
  totalCarbonTonnes: string;
  totalTrees: string;
  totalTreeTransactions: string;
}): ImpactPropertiesReturn => {
  const [impactProperties, setImpactProperties] =
    useState<ImpactPropertiesReturn>({
      totalTrees: profile.totalTrees,
      totalTreeTransactions: profile.totalTreeTransactions,
      totalCarbonTonnes: profile.totalCarbonTonnes,
      projectTreesContributionAggregate:
        profile.projectTreesContributionAggregate,
    });

  useEffect(() => {
    setImpactProperties((prevState) => ({
      ...prevState,
      ...(projectTreesContributionAggregate && {
        projectTreesContributionAggregate: JSON.parse(
          projectTreesContributionAggregate
        ),
      }),
      ...(totalTrees && { totalTrees: Number(totalTrees) }),
      ...(totalTreeTransactions && {
        totalTreeTransactions: Number(totalTreeTransactions),
      }),
      ...(totalCarbonTonnes && {
        totalCarbonTonnes: Number(totalCarbonTonnes),
      }),
    }));
  }, [
    totalTrees,
    totalTreeTransactions,
    totalCarbonTonnes,
    projectTreesContributionAggregate,
  ]);

  return impactProperties;
};
